import type { CityConfig } from "../city-template/types";

export const comptonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Compton",
  state: "California",
  stateAbbr: "CA",
  slug: "compton",
  route: "/cities/compton",
  title: "Emergency Passport Compton, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Compton, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for South Bay Los Angeles travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Compton, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Compton and South Bay travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Compton%20California%20urban%20cityscape%20at%20golden%20hour%20with%20modern%20residential%20neighborhoods%20and%20commercial%20boulevards%2C%20South%20Bay%20Los%20Angeles%20skyline%20visible%20in%20the%20distance%2C%20warm%20sunset%20light%20reflecting%20on%20the%20streets%2C%20professional%20urban%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20suburban%20community%20with%20palm%20trees%20and%20clean%20modern%20environment%2C%20warm%20amber%20and%20gold%20tones%20over%20the%20landscape&width=1800&height=700&seq=compton-hero-001&orientation=landscape",
  heroImageAlt: "Compton California urban cityscape at golden hour with South Bay Los Angeles skyline",
  geoPosition: "33.8958;-118.2201",
  geoPlacename: "Compton, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 25–35 minutes from Compton via I-110 and I-405",
  },
  nearbyCitiesZips: [
    { name: "Compton", zips: "90220, 90221, 90222, 90223" },
    { name: "Lynwood", zips: "90262" },
    { name: "Paramount", zips: "90723" },
    { name: "South Gate", zips: "90280" },
    { name: "Gardena", zips: "90247, 90248, 90249" },
    { name: "Carson", zips: "90745, 90746, 90747, 90749, 90895" },
    { name: "Long Beach", zips: "90802, 90804, 90805, 90806, 90807, 90808, 90810, 90813, 90814, 90815" },
    { name: "Watts", zips: "90002, 90059" },
    { name: "Downey", zips: "90240, 90241, 90242" },
    { name: "Bellflower", zips: "90706" },
    { name: "Norwalk", zips: "90650, 90651, 90652" },
    { name: "Hawaiian Gardens", zips: "90716" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent South Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Long Beach", route: "/cities/long-beach" },
    { name: "Torrance", route: "/cities/torrance" },
    { name: "Santa Monica", route: "/cities/santa-monica" },
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Pasadena", route: "/cities/pasadena" },
    { name: "Glendale", route: "/cities/glendale" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Compton remotely via secure courier. We do not have a walk-in office in Compton — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for South Bay travelers.",
  fpcOfficeAddress: "601 W Ocean Blvd, Long Beach, CA 90802",
  fpcOfficeDistance: "Serving all of South Bay Los Angeles",
  serviceAreaIntro: "From the historic downtown to the residential neighborhoods along the Gateway Cities corridor, Fast Passport Center serves the entire Compton community. We regularly help travelers from Lynwood, Paramount, South Gate, and Carson get their passports fast.",
  nearbyCitiesIntro: "We serve all Compton neighborhoods and nearby South Bay cities including Lynwood, Paramount, South Gate, Gardena, Carson, and Long Beach. If you are in southeastern LA County, we have you covered.",
  howItWorksIntro: "Most Compton travelers complete everything online and ship documents overnight. No need to drive downtown or wait in line at the regional agency.",
  mistakesIntro: "With LAX just 15 miles away serving as the primary international gateway, Compton travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "South Bay and Gateway Cities travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Compton? Here are the most common ones we hear from South Bay travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent South Bay international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Angela M.",
      location: "Compton, CA",
      rating: 5,
      text: "My cruise to the Caribbean was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our entire family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Tyrone J.",
      location: "Lynwood, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa W.",
      location: "South Gate, CA",
      rating: 5,
      text: "Lost my passport the week before my trip to Mexico. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David K.",
      location: "Carson, CA",
      rating: 5,
      text: "As a frequent international traveler from the South Bay, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "compton-1",
      name: "Compton Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "701 S Santa Fe Ave",
      city: "Compton",
      state: "CA",
      zip: "90221",
      phone: "(310) 631-8524",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Compton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "compton-2",
      name: "Lynwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11200 Long Beach Blvd",
      city: "Lynwood",
      state: "CA",
      zip: "90262",
      phone: "(310) 635-2294",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from Compton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "compton-3",
      name: "Paramount Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7200 Somerset Blvd",
      city: "Paramount",
      state: "CA",
      zip: "90723",
      phone: "(562) 634-4224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.8 miles from Compton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Gateway Cities residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "compton-4",
      name: "Long Beach Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2300 Redondo Ave",
      city: "Long Beach",
      state: "CA",
      zip: "90806",
      phone: "(562) 426-5564",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.5 miles from Compton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for South Bay. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "compton-5",
      name: "South Gate Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4655 Firestone Blvd",
      city: "South Gate",
      state: "CA",
      zip: "90280",
      phone: "(323) 569-4264",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.2 miles from Compton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southeastern LA County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "compton-6",
      name: "LA County Clerk — Norwalk",
      type: "County Clerk Passport Acceptance",
      address: "12400 Imperial Hwy",
      city: "Norwalk",
      state: "CA",
      zip: "90650",
      phone: "(562) 462-2133",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "8.0 miles from Compton",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "South Bay Coverage", agency: "No regional expertise", fpc: "Gateway Cities and South Bay LA community travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "South Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Compton acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Compton applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many South Bay families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Compton travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Compton." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Compton address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Compton before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Compton for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most South Bay travelers." },
    { q: "How fast can I renew my passport in Compton?", a: "Fast Passport Center offers fast passport renewal in Compton in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Compton in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Compton?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Compton residents." },
    { q: "Can I expedite a child passport in Compton?", a: "Yes. Fast Passport Center expedites child passports in Compton for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Compton with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Compton?",
      a: "With Fast Passport Center's emergency courier service, Compton travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Compton?",
      a: "Popular options include the Compton Main Post Office (90221), Lynwood Post Office (90262), Paramount Post Office (90723), and LA County Clerk in Norwalk (90650). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Compton?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the Gateway Cities or near the Port of Long Beach?",
      a: "We serve all of southeastern LA County including Lynwood, Paramount, South Gate, Carson, and Long Beach. Our overnight shipping labels work from any UPS location, so your location within the South Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Compton?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Compton?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Compton, Lynwood, and Long Beach offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Compton?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Compton?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk South Bay families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Compton", address: "2101 N Long Beach Blvd, Compton, CA 90221", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — Lynwood", address: "10301 Long Beach Blvd, Lynwood, CA 90262", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — Long Beach", address: "3950 Cherry Ave, Long Beach, CA 90807", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.office.fedex.com/" },
    { name: "UPS Store — Paramount", address: "14442 Paramount Blvd, Paramount, CA 90723", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — South Gate", address: "4651 Firestone Blvd, South Gate, CA 90280", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/photo" },
    { name: "Rite Aid — Carson", address: "20750 Avalon Blvd, Carson, CA 90746", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/photo" },
    { name: "Postal Annex — Gardena", address: "17009 S Western Ave, Gardena, CA 90247", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — Norwalk", address: "12540 Norwalk Blvd, Norwalk, CA 90650", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/photo" },
  ],
  dmvLocations: [
    {
      name: "DMV — Compton",
      address: "2111 S Alameda St, Compton, CA 90221",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Long Beach",
      address: "3707 E South St, Long Beach, CA 90805",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Bellflower",
      address: "9520 Artesia Blvd, Bellflower, CA 90706",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Norwalk",
      address: "12400 Imperial Hwy, Norwalk, CA 90650",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service mega center. Longest hours, most services.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "15 miles from Compton via I-105",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "The primary international airport for Compton. Major hub for international flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: true,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "12 miles from Compton",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for South Bay residents. Domestic flights with some international connections. Less crowded than LAX.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "25 miles from Compton",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Smaller airport with domestic flights and some international connections. Alternative to LAX for LA County travelers.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Los Angeles County residents.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in Los Angeles County.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Los Angeles County Registrar-Recorder/County Clerk. Certified copies available for name changes and legal purposes in Compton and the South Bay.",
      url: "https://www.lavote.gov/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Los Angeles County Superior Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.lacourt.org/",
    },
  ],
};